import { pick } from "lodash-es";
import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { PermissionForm } from "Bands/containers";
import { useBandContext, usePermissionsContext } from "Bands/contexts";
import CopyPermissionsModal from "Bands/pages/copy-permissions/CopyPermissionsModal";
import { BAND_PATHS } from "Bands/router";
import {
  dataMetaSelector,
  dispatchGetBandAction,
  editUserPermissions,
} from "Bands/state";
import { BAND_ACTIONS, IBandUser } from "Bands/types";
import {
  useBandRoute,
  useClearActionMetaOnUnMount,
  useLastMemberWithPermissionError,
} from "Bands/utils";
import { copy } from "shared/assets";
import { Lock } from "shared/assets/icons";
import { Card } from "shared/components";
import { addTemporaryToast } from "shared/components/Toast";
import { API_ENDPOINTS } from "shared/constants";
import { Spacer } from "shared/layout";
import { IAPIState, IPermission, IStatus } from "shared/types";
import { forceIsDirtyAtom } from "../state/permissions";
import { useSetAtom } from "jotai";
const {
  permissions: { editPermissions: editPermissionsCopy },
} = copy;

interface EditPermissionsProps {
  user: IBandUser;
  setForceUserId: (id: string) => void;
  userPermissions: IPermission[];
}

export const EditPermissions: FC<EditPermissionsProps> = ({
  user,
  userPermissions,
  setForceUserId,
}) => {
  const dispatch = useDispatch();
  const [hasbeenSubmitted, setHasbeenSubmitted] = useState(false);
  const [nextLocation, setNextLocation] = useState<any>(null);
  const [withRedirect, setWithRedirect] = useState(false);
  const { permissionTypes } = usePermissionsContext();
  const { bandId, currentBandUser } = useBandContext();
  const { pushRoute, getPath } = useBandRoute();
  const setForceIsDirty = useSetAtom(forceIsDirtyAtom);
  const { api: apiMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.editUserPermissions)
  );

  useClearActionMetaOnUnMount(BAND_ACTIONS.editUserPermissions);

  const handleSubmit = (data: { [id: string]: boolean }) => {
    // don't send values of groups
    // only send TRUE values
    const allowedIds = Object.keys(data).filter(
      (id) => id.includes(API_ENDPOINTS.PERMISSION_TYPES) && data[id]
    );
    const idList = Object.keys(pick(data, allowedIds));

    const cleanData = {
      permissions: idList,
    };

    dispatch(
      editUserPermissions({
        userId: user.id,
        data: cleanData,
      })
    );
  };

  const { error } = useLastMemberWithPermissionError(
    BAND_ACTIONS.editUserPermissions
  );

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      setForceIsDirty(false);
      if (!hasbeenSubmitted) {
        setHasbeenSubmitted(true);
        dispatch(
          addTemporaryToast({
            status: IStatus.success,
            message: editPermissionsCopy.messages.success,
          })
        );
      }

      if (withRedirect) {
        pushRoute(nextLocation.pathname);
        return;
      }

      pushRoute(BAND_PATHS.MEMBERS);

      // If I'm editing permissions for myself, refetch my band permissions
      if (currentBandUser?.id === user?.id && bandId) {
        dispatchGetBandAction(dispatch, bandId);
      }
    }
  }, [
    apiMeta,
    bandId,
    currentBandUser?.id,
    dispatch,
    user?.id,
    withRedirect,
    nextLocation,
    pushRoute,
    hasbeenSubmitted,
    setForceIsDirty,
  ]);

  const handleNextLocation = () => {
    window.location.href = getPath(nextLocation.pathname);
  };

  if (!permissionTypes) return null;

  return (
    <Card
      icon={Lock}
      title={`${editPermissionsCopy.title} ${user.firstname} ${user.lastname}`}
    >
      <Spacer size="xl">
        <CopyPermissionsModal
          selectedMemberId={user.id}
          setForceUserId={setForceUserId}
        />
        <PermissionForm
          onSubmit={handleSubmit}
          permissions={userPermissions}
          permissionTypes={permissionTypes}
          isSubmitting={apiMeta?.status === IAPIState.pending}
          error={error}
          apiError={apiMeta?.error}
          setWithRedirect={setWithRedirect}
          nextLocation={nextLocation}
          setNextLocation={setNextLocation}
          hasbeenSubmitted={hasbeenSubmitted}
          handleNextLocation={handleNextLocation}
        />
      </Spacer>
    </Card>
  );
};
